import { Navigate, Outlet } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Loader2 } from 'lucide-react'

interface ProtectedRouteProps {
  requiredPermission?: string
  requiredRoles?: string[]
  redirectTo?: string
}

export function ProtectedRoute({ 
  requiredPermission,
  requiredRoles,
  redirectTo = '/auth/login' 
}: ProtectedRouteProps) {
  const { user, profile, organization, loading, canAccess, hasRole } = useAuth()
  
  // Check for demo mode
  const isDemoMode = localStorage.getItem('demoMode') === 'true'
  const demoUser = localStorage.getItem('demoUser')
  
  // Show loading spinner while checking auth
  if (loading && !isDemoMode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }
  
  // Allow access if authenticated OR in demo mode
  if (user || (isDemoMode && demoUser)) {
    // Skip other checks for demo mode
    if (isDemoMode) {
      return <Outlet />
    }
  } else {
    // Redirect to login if not authenticated and not in demo mode
    return <Navigate to={redirectTo} replace />
  }
  
  // Check if email is verified
  if (!user.email_confirmed_at) {
    return <Navigate to="/auth/verify-email" replace />
  }
  
  // Check permission-based access
  if (requiredPermission && !canAccess(requiredPermission)) {
    return <Navigate to="/unauthorized" replace />
  }
  
  // Check role-based access
  if (requiredRoles && !hasRole(requiredRoles)) {
    return <Navigate to="/unauthorized" replace />
  }
  
  // Check if organization is active
  if (organization?.subscription_status === 'canceled') {
    return <Navigate to="/billing/reactivate" replace />
  }
  
  return <Outlet />
}